import { create } from 'zustand';
import { http } from '@/libs/http';

export interface AnalyticsSummary {
    metrics: {
        users: number;
        tenants: number;
        applications: number;
        roles: number;
        monthly_active_users: number;
        system_uptime: number;
    };
    activity: {
        last_7_days: number;
    };
    status: string;
    reason?: string;
}

export interface TenantAnalytics {
    window_days: number;
    dau: number;
    mau: number;
    overview: Array<{
        tenant_id: string;
        tenant_name: string;
        tenant_slug: string;
        active_users: number;
        enabled_apps: number;
        enabled_idps: number;
        pending_onboarding: number;
    }>;
    trend: Array<{
        day: string;
        onboarding_requests: number;
    }>;
    top_active_users: Array<{
        actor: string;
        login_count: number;
    }>;
    auth_time_series: Array<{
        day: string;
        successes: number;
        failures: number;
    }>;
}

export interface ApplicationAnalytics {
    window_days: number;
    dau: number;
    mau: number;
    avg_auth_latency_ms: number;
    p95_auth_latency_ms: number;
    applications: Array<{
        id: string;
        name: string;
        status: number;
        enabled_tenants: number;
        health_status: string | null;
        service_principals: number;
    }>;
    activity: Array<{
        day: string;
        events: number;
    }>;
}

export interface IdpAnalytics {
    window_days: number;
    avg_auth_latency_ms: number;
    providers: Array<{
        id: string;
        display_name: string;
        type: string;
        enabled: boolean;
        enabled_configurations: number;
        total_configurations: number;
    }>;
    auth_events: Array<{
        day: string;
        successes: number;
        failures: number;
    }>;
    tenant_breakdown: Array<{
        display_name: string;
        tenant_id: string;
        tenant_name: string;
        enabled_configs: number;
        total_configs: number;
    }>;
}

interface AnalyticsState {
    summary: AnalyticsSummary | null;
    tenant: TenantAnalytics | null;
    application: ApplicationAnalytics | null;
    idp: IdpAnalytics | null;
    isLoading: boolean;
    error: string | null;
    
    // Core actions
    fetchSummary: () => Promise<AnalyticsSummary | null>;
    fetchTenantAnalytics: (days?: number) => Promise<TenantAnalytics | null>;
    fetchApplicationAnalytics: (days?: number) => Promise<ApplicationAnalytics | null>;
    fetchIdpAnalytics: (days?: number) => Promise<IdpAnalytics | null>;
    fetchQuery: <T = unknown>(metric: 'summary' | 'tenants' | 'applications' | 'idps', days?: number) => Promise<T | null>;
}

export const useAnalytics = create<AnalyticsState>((set) => ({
    summary: null,
    tenant: null,
    application: null,
    idp: null,
    isLoading: false,
    error: null,

    fetchSummary: async () => {
        set({ isLoading: true, error: null });
        try {
            const data = await http.get<AnalyticsSummary>('/mgmt/v1/analytics');
            set({ summary: data, isLoading: false });
            return data;
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Unknown error';
            set({ error: message, isLoading: false });
            return null;
        }
    },

    fetchTenantAnalytics: async (days = 30) => {
        try {
            const data = await http.get<TenantAnalytics>(`/mgmt/v1/analytics/tenants?days=${days}`);
            set({ tenant: data });
            return data;
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Unknown error';
            set({ error: message });
            return null;
        }
    },

    fetchApplicationAnalytics: async (days = 30) => {
        try {
            const data = await http.get<ApplicationAnalytics>(`/mgmt/v1/analytics/applications?days=${days}`);
            set({ application: data });
            return data;
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Unknown error';
            set({ error: message });
            return null;
        }
    },

    fetchIdpAnalytics: async (days = 30) => {
        try {
            const data = await http.get<IdpAnalytics>(`/mgmt/v1/analytics/idps?days=${days}`);
            set({ idp: data });
            return data;
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Unknown error';
            set({ error: message });
            return null;
        }
    },

    fetchQuery: async (metric, days = 30) => {
        try {
            return await http.get(`/mgmt/v1/analytics/query?metric=${metric}&days=${days}`);
        } catch (err: unknown) {
            const message = err instanceof Error ? err.message : 'Unknown error';
            set({ error: message });
            return null;
        }
    },
}));
